/**
 * Human-readable reverse-bid / deal stage for admin (BidLog row).
 */
export function getDealStageLabel(bid) {
  if (!bid) return "—";
  if (bid.treatmentCompletedAt) return "Treatment completed";
  if (bid.providerAction === "rejected" || bid.patientAction === "rejected") {
    return "Declined";
  }
  if (bid.providerAction === "pending") {
    return "Awaiting provider";
  }
  if (bid.providerAction === "accepted_patient_price") {
    if (bid.patientAction === "accepted") return "Agreed";
    return "Doctor accepted patient price";
  }
  if (bid.providerAction === "countered") {
    if (bid.patientAction === "pending") return "Counter offer — patient to respond";
    if (bid.patientAction === "accepted") return "Agreed — proceed to booking";
    if (bid.patientAction === "countered") return "Negotiating";
  }
  return "In progress";
}

export function summarizePatientBids(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const doctors = list.filter((b) => b.providerRole === "doctor");
  const hospitals = list.filter((b) => b.providerRole === "hospital");
  const doctorIds = new Set(doctors.map((b) => String(b.providerId ?? "")));
  const hospitalIds = new Set(hospitals.map((b) => String(b.providerId ?? "")));
  doctorIds.delete("");
  hospitalIds.delete("");

  return {
    totalBids: list.length,
    doctorCount: doctorIds.size,
    hospitalCount: hospitalIds.size,
    rows: list,
  };
}

/**
 * One list item per provider; each item has all BidLog rows for that doctor/hospital
 * (patient can match multiple procedures → multiple BidLogs, same provider).
 */
export function groupBidRowsByProvider(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const order = [];
  const map = new Map();
  for (const b of list) {
    const key = `${String(b.providerRole || "")}:${String(b.providerId ?? "")}`;
    if (!map.has(key)) {
      map.set(key, {
        providerRole: b.providerRole,
        providerId: b.providerId,
        providerName: b.providerName,
        lines: [],
      });
      order.push(key);
    }
    map.get(key).lines.push(b);
  }
  return order.map((k) => map.get(k));
}

/** Single badge when a provider has several procedure rows and the same stage on all. */
export function combinedDealStageLabel(lines) {
  const list = Array.isArray(lines) ? lines : [];
  if (!list.length) return "—";
  const labels = list.map((b) => getDealStageLabel(b));
  const uniq = [...new Set(labels)];
  if (uniq.length === 1) return uniq[0];
  return `${list.length} matched procedures`;
}

